import { useEffect, useState } from "react";
import api from "@/api";
import { useNoctua } from "./NoctuaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calculator, Zap } from "lucide-react";

export default function EscenariosPage() {
  const { partidos, clientParty, selectedState, scopeFilter } = useNoctua();
  const [municipios, setMunicipios] = useState([]);
  const [muniId, setMuniId] = useState("");
  const [form, setForm] = useState({
    participacion_estim_pct: 60,
    visitas_por_dia: 5,
    conversion_pct: 30,
    dias_campana: 60,
    meta_pct: 50,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const isMunicipalScope = scopeFilter?.scope_type === "municipal" && scopeFilter?.municipio_clave;
  const isDistrictScope = scopeFilter?.scope_type === "distrital_federal" || scopeFilter?.scope_type === "distrital_local";
  const districtLoaded = isDistrictScope && scopeFilter?.district_mapping_loaded && (scopeFilter.district_municipios_claves?.length > 0);

  useEffect(() => {
    api.get(`/noctua/municipios?estado_clave=${selectedState}&limit=500`).then((r) => {
      let list = r.data;
      if (isMunicipalScope) {
        list = list.filter((m) => Number(m.municipio_clave) === Number(scopeFilter.municipio_clave));
      } else if (districtLoaded) {
        const allowed = new Set(scopeFilter.district_municipios_claves.map(Number));
        list = list.filter((m) => allowed.has(Number(m.municipio_clave)));
      }
      setMunicipios(list);
      // Auto-select the only municipio when municipal scope; otherwise reset
      if (isMunicipalScope && list.length > 0) {
        setMuniId(list[0].id);
      } else {
        setMuniId("");
      }
      setResult(null);
    });
  }, [selectedState, isMunicipalScope, scopeFilter?.municipio_clave, districtLoaded, scopeFilter?.district_municipios_claves]);

  const calc = async () => {
    if (!muniId || !clientParty) return;
    setLoading(true);
    try {
      const r = await api.post("/noctua/escenario", {
        municipio_id: muniId,
        partido: clientParty,
        ...form,
        participacion_estim_pct: parseFloat(form.participacion_estim_pct),
        visitas_por_dia: parseInt(form.visitas_por_dia, 10),
        conversion_pct: parseFloat(form.conversion_pct),
        dias_campana: parseInt(form.dias_campana, 10),
        meta_pct: parseFloat(form.meta_pct),
      });
      setResult(r.data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || "Error" });
    } finally {
      setLoading(false);
    }
  };

  const viabilityColor = {
    GANANDO: "#16A34A",
    ALTO: "#3B82F6",
    MEDIO: "#EAB308",
    BAJO: "#DC2626",
  };

  return (
    <div className="space-y-6" data-testid="noctua-escenarios-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Motor estratégico</div>
        <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl text-white">Motor de Escenarios</h1>
        <p className="text-sm text-slate-400 mt-1">
          Calcula contactos, promotores y días para cambiar tendencia · Partido: <strong className="text-white">{clientParty || "—"}</strong>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Parameters */}
        <div className="noctua-card p-5">
          <h3 className="font-display font-bold text-lg text-white flex items-center gap-2"><Calculator className="w-4 h-4 text-blue-400" /> Parámetros</h3>
          <div className="mt-4 space-y-3">
            <Field label="Municipio objetivo">
              <Select value={muniId} onValueChange={setMuniId}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="esc-muni-select"><SelectValue placeholder="Selecciona municipio" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {municipios.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-white">{m.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="% participación estimada">
              <Input type="number" min={10} max={100} className="bg-slate-900 border-slate-700 text-white" value={form.participacion_estim_pct} onChange={(e) => setForm({ ...form, participacion_estim_pct: e.target.value })} data-testid="esc-participacion" />
            </Field>
            <Field label="Visitas por día / promotor">
              <Input type="number" min={1} className="bg-slate-900 border-slate-700 text-white" value={form.visitas_por_dia} onChange={(e) => setForm({ ...form, visitas_por_dia: e.target.value })} data-testid="esc-visitas" />
            </Field>
            <Field label="% conversión (dudoso → SÍ)">
              <Input type="number" min={1} max={100} className="bg-slate-900 border-slate-700 text-white" value={form.conversion_pct} onChange={(e) => setForm({ ...form, conversion_pct: e.target.value })} data-testid="esc-conversion" />
            </Field>
            <Field label="Días de campaña">
              <Input type="number" min={1} className="bg-slate-900 border-slate-700 text-white" value={form.dias_campana} onChange={(e) => setForm({ ...form, dias_campana: e.target.value })} data-testid="esc-dias" />
            </Field>
            <Field label="% meta de voto">
              <Input type="number" min={10} max={100} className="bg-slate-900 border-slate-700 text-white" value={form.meta_pct} onChange={(e) => setForm({ ...form, meta_pct: e.target.value })} data-testid="esc-meta" />
            </Field>
            <Button onClick={calc} disabled={loading || !muniId || !clientParty} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="esc-calc-button">
              {loading ? "Calculando…" : <><Zap className="w-4 h-4 mr-2" /> Calcular escenario</>}
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-2">
          {!result ? (
            <div className="noctua-card p-8 text-center text-slate-500 min-h-[400px] grid place-items-center">
              <div>
                <Calculator className="w-10 h-10 mx-auto text-slate-700" />
                <p className="mt-3">Selecciona un municipio y calcula el escenario.</p>
              </div>
            </div>
          ) : result.error ? (
            <div className="noctua-card p-6 border-l-4 border-l-red-500">
              <p className="text-red-300">{result.error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="noctua-card p-5">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Escenario</div>
                    <div className="font-display font-black text-2xl text-white mt-1">{result.municipio} · {result.partido}</div>
                  </div>
                  <span
                    className="px-3 py-1 rounded font-display font-bold tracking-wider text-sm uppercase"
                    style={{ backgroundColor: `${viabilityColor[result.viabilidad]}30`, color: viabilityColor[result.viabilidad] }}
                    data-testid="esc-viability"
                  >
                    {result.viabilidad}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <MetricCard label="Contactos necesarios" value={result.contactos_necesarios.toLocaleString()} testid="esc-contactos" accent="blue" />
                <MetricCard label="Promotores mínimos" value={result.promotores_minimos} testid="esc-promotores" accent="blue" />
                <MetricCard label="Días con 1 promotor" value={result.dias_con_1_promotor.toLocaleString()} testid="esc-dias-1prom" />
                <MetricCard label="Votos objetivo" value={result.votos_objetivo.toLocaleString()} testid="esc-votos-obj" />
                <MetricCard label="Votos actuales" value={result.votos_actuales.toLocaleString()} testid="esc-votos-act" />
                <MetricCard label="Gap a cerrar" value={result.gap.toLocaleString()} testid="esc-gap" accent="red" />
              </div>

              <div className="noctua-card p-5 border-l-4 border-l-blue-500">
                <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Análisis estratégico</div>
                <p className="mt-2 text-sm text-slate-200 leading-relaxed">{result.narrativa}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function MetricCard({ label, value, accent, testid }) {
  const accentColor = accent === "blue" ? "border-l-blue-500" : accent === "red" ? "border-l-red-500" : "border-l-slate-600";
  return (
    <div className={`noctua-card p-4 border-l-4 ${accentColor}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="mt-2 font-display font-black text-2xl text-white tabular-nums">{value}</div>
    </div>
  );
}
